/**
 * Meta Pixel & Google Analytics Event Tracking Utility
 * Centralized tracking for all conversion events across the website.
 *
 * Meta Pixel Events:
 * - Schedule: Standard event for booking/appointment actions
 * - Contact: Standard event for WhatsApp/phone contact
 * - Lead: Standard event for form submissions
 * - ViewContent: Standard event for service views
 *
 * Custom Events:
 * - BookNow_Click: Custom event for any "Book Now" button click
 * - WhatsApp_Click: Custom event for WhatsApp button clicks
 * - Phone_Click: Custom event for phone call button clicks
 * - BookingForm_Submit: Custom event for booking form submissions
 * - ExitIntent_Claim: Custom event for exit intent popup conversions
 */

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    gtag?: (...args: unknown[]) => void;
  }
}

type TrackingLocation =
  | "hero"
  | "navbar"
  | "mobile_menu"
  | "promo_banner"
  | "cta_banner"
  | "services"
  | "doctors"
  | "footer"
  | "floating_button"
  | "exit_intent"
  | "booking_form"
  | "before_after"
  | "offers"
  | "blog"
  | "not_found"
  | "why_bella_rose"
  | "services_page"
  | "quick_access_strip"
  | "faq_index"
  | "faq_service"
  | "blog_article"
  | "guide"
  | "services_page_form"
  | "offers_page_form"
  | "blog_index_form"
  | "blog_article_form"
  | "guides_index_form"
  | "guide_page_form"
  | "faq_index_form"
  | "faq_service_form"
  | "reviews_page_form"
  | "why_bella_rose_form"
  | "best_dental_clinic"
  | "best_dermatology_clinic"
  | "weight_loss_programs"
  | "laser_hair_removal"
  | "cosmetic_surgery_clinics"
  | "doctors_page_form"
  | "doctors_page_card"
  | "doctors_page_call_button"
  | "dr_samira_page"
  | "sticky_mobile_cta";

// ─── Meta Pixel Tracking ───────────────────────────────────────────

function fbqTrack(event: string, params?: Record<string, unknown>) {
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("track", event, params);
  }
}

function fbqTrackCustom(event: string, params?: Record<string, unknown>) {
  if (typeof window !== "undefined" && window.fbq) {
    window.fbq("trackCustom", event, params);
  }
}

// ─── Google Analytics Tracking ─────────────────────────────────────

function gtagEvent(action: string, params?: Record<string, unknown>) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", action, params);
  }
}

// ─── Public Tracking Functions ─────────────────────────────────────

/**
 * Track "Book Now" button clicks (WhatsApp booking links)
 */
export function trackBookNow(location: TrackingLocation, service?: string) {
  // Meta Pixel - Standard Schedule event
  fbqTrack("Schedule", {
    content_name: service || "General Booking",
    content_category: "Appointment",
    source: location,
  });

  // Meta Pixel - Custom event for granular tracking
  fbqTrackCustom("BookNow_Click", {
    location,
    service: service || "general",
  });

  // Google Analytics
  gtagEvent("book_now_click", {
    event_category: "conversion",
    event_label: location,
    service: service || "general",
  });
}

/**
 * Track WhatsApp floating button clicks
 */
export function trackWhatsAppClick(location: TrackingLocation, context?: string) {
  // Meta Pixel - Standard Contact event
  fbqTrack("Contact", {
    content_name: "WhatsApp",
    content_category: "Contact",
    source: location,
  });

  // Meta Pixel - Custom event
  fbqTrackCustom("WhatsApp_Click", {
    location,
    context: context || "general",
  });

  // Google Analytics
  gtagEvent("whatsapp_click", {
    event_category: "contact",
    event_label: location,
    context: context || "general",
  });
}

/**
 * Track phone call button clicks
 */
export function trackPhoneClick(location: TrackingLocation) {
  // Meta Pixel - Standard Contact event
  fbqTrack("Contact", {
    content_name: "Phone Call",
    content_category: "Contact",
    source: location,
  });

  // Meta Pixel - Custom event
  fbqTrackCustom("Phone_Click", {
    location,
  });

  // Google Analytics
  gtagEvent("phone_click", {
    event_category: "contact",
    event_label: location,
  });
}

/**
 * Track booking form submissions
 */
export function trackBookingFormSubmit(service: string, location: TrackingLocation) {
  // Meta Pixel - Standard Lead event
  fbqTrack("Lead", {
    content_name: service,
    content_category: "Booking Form",
    source: location,
  });

  // Meta Pixel - Custom event
  fbqTrackCustom("BookingForm_Submit", {
    service,
    location,
  });

  // Google Analytics
  gtagEvent("booking_form_submit", {
    event_category: "conversion",
    event_label: service,
    source: location,
  });
}

/**
 * Track exit intent popup claim
 */
export function trackExitIntentClaim() {
  // Meta Pixel - Standard Lead event
  fbqTrack("Lead", {
    content_name: "Exit Intent Offer",
    content_category: "Exit Intent",
  });

  // Meta Pixel - Custom event
  fbqTrackCustom("ExitIntent_Claim", {
    offer: "free_consultation_10_percent_off",
  });

  // Google Analytics
  gtagEvent("exit_intent_claim", {
    event_category: "conversion",
    event_label: "exit_intent_popup",
  });
}

/**
 * Track service view (when user clicks on a service category)
 */
export function trackServiceView(serviceName: string) {
  // Meta Pixel - Standard ViewContent event
  fbqTrack("ViewContent", {
    content_name: serviceName,
    content_category: "Service",
    content_type: "medical_service",
  });

  // Google Analytics
  gtagEvent("view_service", {
    event_category: "engagement",
    event_label: serviceName,
  });
}

/**
 * Track doctor booking (when user clicks "Book with Dr. X")
 */
export function trackDoctorBooking(doctorName: string) {
  // Meta Pixel - Standard Schedule event
  fbqTrack("Schedule", {
    content_name: `Booking with ${doctorName}`,
    content_category: "Doctor Appointment",
  });

  // Meta Pixel - Custom event
  fbqTrackCustom("BookNow_Click", {
    location: "doctors",
    doctor: doctorName,
  });

  // Google Analytics
  gtagEvent("book_doctor", {
    event_category: "conversion",
    event_label: doctorName,
  });
}
